import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  registerWithOrganization,
  clearError,
  selectAuthLoading,
  selectAuthError
} from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import {
  User,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  Church,
  Building,
  MapPin,
  MapPinned,
  Globe,
  Clock,
  LoaderCircle,
  CheckCircle,
  XCircle,
  FileText,
  Landmark,
  AlertTriangle
} from 'lucide-react';

// Country data with flags
const countryData = {
  'GH': {
    name: 'Ghana',
    flag: '🇬🇭',
    phoneCode: '+233',
    timezone: 'Africa/Accra',
    states: [
      'Greater Accra',
      'Ashanti',
      'Central',
      'Eastern',
      'Western',
      'Northern',
      'Upper East',
      'Upper West',
      'Volta',
      'Bono',
      'Bono East',
      'Ahafo',
      'Savannah',
      'North East',
      'Oti',
      'Western North'
    ]
  }
  // Add other countries as needed...
};

// Password validation helper
const validatePassword = (password) => {
  const errors = [];
  if (password.length < 8) errors.push('At least 8 characters');
  if (!/[A-Z]/.test(password)) errors.push('At least one uppercase letter');
  if (!/[a-z]/.test(password)) errors.push('At least one lowercase letter');
  if (!/[0-9]/.test(password)) errors.push('At least one number');
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('At least one special character (!@#$%^&*...)');
  return { isValid: errors.length === 0, errors };
};

// Password strength indicator
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: 'Weak', color: '#dc2626' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
  const strengthMap = {
    0: { label: 'Very Weak', color: '#dc2626' },
    1: { label: 'Weak', color: '#dc2626' },
    2: { label: 'Fair', color: '#f59e0b' },
    3: { label: 'Good', color: '#f59e0b' },
    4: { label: 'Strong', color: '#10b981' },
    5: { label: 'Very Strong', color: '#10b981' }
  };
  return { score, ...strengthMap[score] };
};

const Register = ({ showToast }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    organization: {
      name: '',
      description: '',
      phone: '',
      email: '',
      address: '',
      country: 'GH',
      state: '',
      city: '',
      timezone: 'Africa/Accra',
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiMessage, setApiMessage] = useState('');
  const [apiError, setApiError] = useState('');
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState('GH');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: 'Weak', color: '#dc2626' });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // Update states when country changes
  useEffect(() => {
    const country = countryData[selectedCountryCode];
    if (country) {
      setAvailableStates(country.states || []);
      const currentState = formData.organization.state;
      if (currentState && !country.states.includes(currentState)) {
        setFormData(prev => ({
          ...prev,
          organization: { ...prev.organization, state: '' }
        }));
      }
    }
  }, [selectedCountryCode]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
      setApiMessage('');
      setApiError('');
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Organization fields
    if (name.startsWith('org_')) {
      const orgField = name.replace('org_', '');
      if (orgField === 'country') {
        const country = countryData[value];
        if (country) {
          setSelectedCountryCode(value);
          setFormData(prev => ({
            ...prev,
            organization: {
              ...prev.organization,
              country: value,
              timezone: country.timezone,
              state: ''
            }
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          organization: { ...prev.organization, [orgField]: value }
        }));
      }
    } else {
      // User fields
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Real‑time password validation
    if (name === 'password') {
      const validation = validatePassword(value);
      setPasswordErrors(validation.errors);
      const strength = getPasswordStrength(value);
      setPasswordStrength(strength);
    }

    // Clear field errors on change
    const errorKey = name.startsWith('org_') ? name.replace('org_', '') : name;
    if (fieldErrors[errorKey]) {
      setFieldErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
    setApiError('');
    setApiMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setApiError('');
    setApiMessage('');

    // ---- Validation ----
    // User fields
    if (!formData.first_name) {
      setFieldErrors(prev => ({ ...prev, first_name: 'First name is required' }));
      return;
    }
    if (!formData.last_name) {
      setFieldErrors(prev => ({ ...prev, last_name: 'Last name is required' }));
      return;
    }
    if (!formData.email) {
      setFieldErrors(prev => ({ ...prev, email: 'Email is required' }));
      return;
    }
    if (!formData.phone) {
      setFieldErrors(prev => ({ ...prev, phone: 'Phone number is required' }));
      return;
    }
    if (!formData.password) {
      setFieldErrors(prev => ({ ...prev, password: 'Password is required' }));
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setFieldErrors(prev => ({ ...prev, password: 'Password does not meet requirements' }));
      setPasswordErrors(passwordValidation.errors);
      return;
    }
    if (formData.password !== formData.password_confirmation) {
      setFieldErrors(prev => ({ ...prev, password_confirmation: 'Passwords do not match' }));
      return;
    }

    // Organization fields
    if (!formData.organization.name) {
      setFieldErrors(prev => ({ ...prev, 'organization.name': 'Church name is required' }));
      return;
    }
    if (!formData.organization.phone) {
      setFieldErrors(prev => ({ ...prev, 'organization.phone': 'Church phone is required' }));
      return;
    }
    if (!formData.organization.email) {
      setFieldErrors(prev => ({ ...prev, 'organization.email': 'Church email is required' }));
      return;
    }
    if (!formData.organization.address) {
      setFieldErrors(prev => ({ ...prev, 'organization.address': 'Address is required' }));
      return;
    }
    if (!formData.organization.country) {
      setFieldErrors(prev => ({ ...prev, 'organization.country': 'Country is required' }));
      return;
    }
    if (!formData.organization.state) {
      setFieldErrors(prev => ({ ...prev, 'organization.state': 'State/Region is required' }));
      return;
    }
    if (!formData.organization.city) {
      setFieldErrors(prev => ({ ...prev, 'organization.city': 'City is required' }));
      return;
    }

    // ---- Prepare submit data ----
    const country = countryData[formData.organization.country];
    const submitData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      password_confirmation: formData.password_confirmation,
      organization: {
        name: formData.organization.name,
        description: formData.organization.description,
        phone: formData.organization.phone,
        email: formData.organization.email,
        address: formData.organization.address,
        country: country ? country.name : formData.organization.country,
        state: formData.organization.state,
        city: formData.organization.city,
        timezone: formData.organization.timezone,
      }
    };

    try {
      const result = await dispatch(registerWithOrganization(submitData)).unwrap();

      const phoneNumber = result.data?.phone || formData.phone;
      setApiMessage(result.message || 'Registration successful!');

      if (showToast) {
        showToast(`✅ ${result.message || 'Registration successful! Please verify your phone.'}`, 'success');
      }

      setTimeout(() => {
        navigate('/verify-otp', {
          state: {
            phone: phoneNumber,
            autoSend: true
          }
        });
      }, 1500);

    } catch (err) {
      console.error('❌ Registration failed:', err);
      if (err.errors) setFieldErrors(err.errors);
      const errorMsg = err.message || 'Registration failed';
      setApiError(errorMsg);
      if (showToast) showToast(`❌ ${errorMsg}`, 'error');
    }
  };

  // ---- Styles (matching Register component) ----
  const inputStyles = {
    width: '100%',
    padding: '16px 20px 16px 50px',
    fontSize: '17px',
    borderRadius: '10px',
    border: '2px solid #e2e8f0',
    background: '#ffffff',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: '#1a1a2e',
    outline: 'none',
    lineHeight: '1.5',
  };

  const selectStyles = {
    ...inputStyles,
    cursor: 'pointer',
    appearance: 'auto',
    padding: '16px 20px 16px 50px',
  };

  const textareaStyles = {
    ...inputStyles,
    resize: 'vertical',
    minHeight: '100px',
    paddingTop: '16px',
    paddingBottom: '16px',
    fontFamily: 'inherit',
  };

  const labelStyles = {
    display: 'block',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a1a2e',
    fontSize: '15px',
  };

  const errorStyles = {
    color: '#dc2626',
    fontSize: '14px',
    marginTop: '6px',
    display: 'block',
  };

  const inputWrapperStyles = {
    marginBottom: '20px',
    position: 'relative',
  };

  const iconWrapperStyles = {
    position: 'absolute',
    left: '18px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    pointerEvents: 'none',
    zIndex: 1,
  };

  const textareaIconStyles = {
    position: 'absolute',
    left: '18px',
    top: '18px',
    color: '#94a3b8',
    pointerEvents: 'none',
    zIndex: 1,
  };

  // Password requirement item
  const PasswordRequirement = ({ met, text }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '14px',
      color: met ? '#10b981' : '#dc2626',
      marginBottom: '6px'
    }}>
      {met ? <CheckCircle size={16} /> : <XCircle size={16} />}
      <span>{text}</span>
    </div>
  );

  // Country options
  const countryOptions = Object.keys(countryData).map(code => ({
    value: code,
    label: `${countryData[code].flag} ${countryData[code].name}`,
    flag: countryData[code].flag,
    name: countryData[code].name
  }));

  const timezoneOptions = [
    { value: 'Africa/Accra', label: 'Africa/Accra (GMT+0)' },
    { value: 'Africa/Lagos', label: 'Africa/Lagos (GMT+1)' },
    { value: 'Africa/Nairobi', label: 'Africa/Nairobi (GMT+3)' },
    { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (GMT+2)' },
    { value: 'Africa/Dar_es_Salaam', label: 'Africa/Dar_es_Salaam (GMT+3)' },
    { value: 'Africa/Kampala', label: 'Africa/Kampala (GMT+3)' },
    { value: 'Africa/Kigali', label: 'Africa/Kigali (GMT+2)' },
    { value: 'Africa/Lusaka', label: 'Africa/Lusaka (GMT+2)' },
    { value: 'Africa/Harare', label: 'Africa/Harare (GMT+2)' },
    { value: 'Africa/Gaborone', label: 'Africa/Gaborone (GMT+2)' },
  ];

  return (
    <AuthLayout title="Create Account" subtitle="Join the FaithConnect community" wide={true}>
      <form onSubmit={handleSubmit} style={{ maxWidth: '640px', margin: '0 auto' }}>
        {/* API Success Message */}
        {apiMessage && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            backgroundColor: '#d1fae5',
            borderRadius: '10px',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <CheckCircle size={20} /> {apiMessage}
          </div>
        )}

        {/* API Error Message */}
        {(apiError && !apiError.includes('Bearer token') && !apiError.includes('token')) && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            backgroundColor: '#fee2e2',
            borderRadius: '10px',
            color: '#dc2626',
            border: '1px solid #fecaca',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} /> {apiError}
          </div>
        )}

        {/* Redux Error */}
        {(error && typeof error === 'string' && !error.includes('Bearer token') && !error.includes('token')) && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            backgroundColor: '#fee2e2',
            borderRadius: '10px',
            color: '#dc2626',
            border: '1px solid #fecaca',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} /> {error}
          </div>
        )}

        {/* ===== ORGANIZATION / CHURCH DETAILS ===== */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1a1a2e',
            marginBottom: '20px',
            borderBottom: '2px solid #d4af37',
            paddingBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Church size={22} color="#d4af37" /> Church Details
          </h3>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Church Name *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Building size={20} /></span>
              <input
                type="text"
                name="org_name"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors['organization.name'] ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="Enter church name"
                value={formData.organization.name}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors['organization.name'] ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors['organization.name'] && <span style={errorStyles}>{fieldErrors['organization.name']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Church Phone *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Phone size={20} /></span>
              <input
                type="tel"
                name="org_phone"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors['organization.phone'] ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="024 412 3456"
                value={formData.organization.phone}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors['organization.phone'] ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors['organization.phone'] && <span style={errorStyles}>{fieldErrors['organization.phone']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Church Email *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Mail size={20} /></span>
              <input
                type="email"
                name="org_email"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors['organization.email'] ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="info@faithchurch.org"
                value={formData.organization.email}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors['organization.email'] ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors['organization.email'] && <span style={errorStyles}>{fieldErrors['organization.email']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Address *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><MapPin size={20} /></span>
              <input
                type="text"
                name="org_address"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors['organization.address'] ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="123 Main Street"
                value={formData.organization.address}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors['organization.address'] ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors['organization.address'] && <span style={errorStyles}>{fieldErrors['organization.address']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Country *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Globe size={20} /></span>
              <select
                name="org_country"
                style={{
                  ...selectStyles,
                  borderColor: fieldErrors['organization.country'] ? '#dc2626' : '#e2e8f0'
                }}
                value={formData.organization.country}
                onChange={handleChange}
                required
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors['organization.country'] ? '#dc2626' : '#e2e8f0'}
              >
                {countryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {fieldErrors['organization.country'] && <span style={errorStyles}>{fieldErrors['organization.country']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>State/Region *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Landmark size={20} /></span>
              <select
                name="org_state"
                style={{
                  ...selectStyles,
                  borderColor: fieldErrors['organization.state'] ? '#dc2626' : '#e2e8f0'
                }}
                value={formData.organization.state}
                onChange={handleChange}
                required
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors['organization.state'] ? '#dc2626' : '#e2e8f0'}
              >
                <option value="">Select State/Region</option>
                {availableStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            {fieldErrors['organization.state'] && <span style={errorStyles}>{fieldErrors['organization.state']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>City *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><MapPinned size={20} /></span>
              <input
                type="text"
                name="org_city"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors['organization.city'] ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="Enter city"
                value={formData.organization.city}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors['organization.city'] ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors['organization.city'] && <span style={errorStyles}>{fieldErrors['organization.city']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Timezone</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Clock size={20} /></span>
              <select
                name="org_timezone"
                style={selectStyles}
                value={formData.organization.timezone}
                onChange={handleChange}
              >
                {timezoneOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {fieldErrors['organization.timezone'] && <span style={errorStyles}>{fieldErrors['organization.timezone']}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Description</label>
            <div style={{ position: 'relative' }}>
              <span style={textareaIconStyles}><FileText size={20} /></span>
              <textarea
                name="org_description"
                style={textareaStyles}
                rows="3"
                placeholder="Brief description of your church"
                value={formData.organization.description}
                onChange={handleChange}
              />
            </div>
            {fieldErrors['organization.description'] && <span style={errorStyles}>{fieldErrors['organization.description']}</span>}
          </div>
        </div>

        {/* ===== ADMIN / USER DETAILS ===== */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1a1a2e',
            marginBottom: '20px',
            borderBottom: '2px solid #d4af37',
            paddingBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <User size={22} color="#d4af37" /> Admin Account
          </h3>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>First Name *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><User size={20} /></span>
              <input
                type="text"
                name="first_name"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors.first_name ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="Enter first name"
                value={formData.first_name}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.first_name ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors.first_name && <span style={errorStyles}>{fieldErrors.first_name}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Last Name *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><User size={20} /></span>
              <input
                type="text"
                name="last_name"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors.last_name ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="Enter last name"
                value={formData.last_name}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.last_name ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors.last_name && <span style={errorStyles}>{fieldErrors.last_name}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Email Address *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Mail size={20} /></span>
              <input
                type="email"
                name="email"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors.email ? '#dc2626' : '#e2e8f0'
                }}
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.email ? '#dc2626' : '#e2e8f0'}
              />
            </div>
            {fieldErrors.email && <span style={errorStyles}>{fieldErrors.email}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Phone Number *</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <select
                style={{
                  ...selectStyles,
                  width: '120px',
                  flexShrink: 0,
                  padding: '16px 10px 16px 10px',
                  borderColor: fieldErrors.phone ? '#dc2626' : '#e2e8f0'
                }}
                value={formData.organization.country}
                onChange={(e) => {
                  const country = countryData[e.target.value];
                  if (country) {
                    setFormData(prev => ({
                      ...prev,
                      organization: {
                        ...prev.organization,
                        country: e.target.value
                      }
                    }));
                  }
                }}
              >
                {countryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.flag} {countryData[opt.value].phoneCode}
                  </option>
                ))}
              </select>
              <div style={{ position: 'relative', flex: 1 }}>
                <span style={{ ...iconWrapperStyles, left: '18px' }}><Phone size={20} /></span>
                <input
                  type="tel"
                  name="phone"
                  required
                  style={{
                    ...inputStyles,
                    paddingLeft: '50px',
                    borderColor: fieldErrors.phone ? '#dc2626' : '#e2e8f0'
                  }}
                  placeholder="20 123 4567"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                  onBlur={(e) => e.target.style.borderColor = fieldErrors.phone ? '#dc2626' : '#e2e8f0'}
                />
              </div>
            </div>
            {fieldErrors.phone && <span style={errorStyles}>{fieldErrors.phone}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Password *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Lock size={20} /></span>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors.password ? '#dc2626' : '#e2e8f0',
                  paddingRight: '50px'
                }}
                placeholder="Min 8 characters"
                value={formData.password}
                onChange={handleChange}
                minLength={8}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.password ? '#dc2626' : '#e2e8f0'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px',
                  color: '#94a3b8',
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
              </button>
            </div>

            {/* Password Requirements */}
            {formData.password && (
              <div style={{
                marginTop: '10px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e' }}>
                    Password Requirements:
                  </span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: passwordStrength.color
                  }}>
                    {passwordStrength.label}
                  </span>
                </div>

                <div style={{
                  width: '100%',
                  height: '6px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '3px',
                  marginBottom: '10px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    height: '100%',
                    backgroundColor: passwordStrength.color,
                    transition: 'width 0.3s ease'
                  }} />
                </div>

                <PasswordRequirement
                  met={formData.password.length >= 8}
                  text="At least 8 characters"
                />
                <PasswordRequirement
                  met={/[A-Z]/.test(formData.password)}
                  text="At least one uppercase letter (A-Z)"
                />
                <PasswordRequirement
                  met={/[a-z]/.test(formData.password)}
                  text="At least one lowercase letter (a-z)"
                />
                <PasswordRequirement
                  met={/[0-9]/.test(formData.password)}
                  text="At least one number (0-9)"
                />
                <PasswordRequirement
                  met={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)}
                  text="At least one special character (!@#$%^&*...)"
                />
              </div>
            )}
            {fieldErrors.password && <span style={errorStyles}>{fieldErrors.password}</span>}
          </div>

          <div style={inputWrapperStyles}>
            <label style={labelStyles}>Confirm Password *</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapperStyles}><Lock size={20} /></span>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="password_confirmation"
                required
                style={{
                  ...inputStyles,
                  borderColor: fieldErrors.password_confirmation ? '#dc2626' : '#e2e8f0',
                  paddingRight: '50px'
                }}
                placeholder="Confirm your password"
                value={formData.password_confirmation}
                onChange={handleChange}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.password_confirmation ? '#dc2626' : '#e2e8f0'}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px',
                  color: '#94a3b8',
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
              </button>
            </div>

            {formData.password_confirmation && formData.password && (
              <div style={{ marginTop: '6px' }}>
                {formData.password === formData.password_confirmation ? (
                  <span style={{
                    color: '#10b981',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <CheckCircle size={16} /> Passwords match
                  </span>
                ) : (
                  <span style={{
                    color: '#dc2626',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <XCircle size={16} /> Passwords do not match
                  </span>
                )}
              </div>
            )}
            {fieldErrors.password_confirmation && <span style={errorStyles}>{fieldErrors.password_confirmation}</span>}
          </div>
        </div>

        <div style={{
          marginBottom: '24px',
          padding: '14px 18px',
          backgroundColor: '#fef3c7',
          borderRadius: '8px',
          color: '#92400e',
          fontSize: '14px',
          border: '1px solid #fde68a',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> Your account will need admin approval before you can access the platform.
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '18px',
            fontSize: '18px',
            fontWeight: '700',
            borderRadius: '10px',
            backgroundColor: '#d4af37',
            color: '#1a1a2e',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: loading ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
          onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = '#c5a032'; }}
          onMouseLeave={(e) => { if (!loading) e.target.style.backgroundColor = '#d4af37'; }}
        >
          {loading ? (
            <>
              <LoaderCircle size={22} style={{ animation: 'spin 1s linear infinite' }} />
              Creating account...
            </>
          ) : (
            <>
              <Church size={20} /> Create Church & Account
            </>
          )}
        </button>

        <div style={{
          margin: '24px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px'
        }}>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e2e8f0' }} />
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>OR</span>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e2e8f0' }} />
        </div>

        <p style={{ textAlign: 'center', fontSize: '15px', color: '#64748b' }}>
          Already have an account?{' '}
          <Link to="/login" style={{
            color: '#d4af37',
            fontWeight: '700',
            textDecoration: 'none',
            fontSize: '15px'
          }}>
            Sign in
          </Link>
        </p>
      </form>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </AuthLayout>
  );
};

export default Register;